import useDebouncedValue from 'hooks/useDebouncedValue';
import React from 'react';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import TimelapseIcon from '@material-ui/icons/Timelapse';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import Layout from '../../../Components/layouts';
import { Dashboard } from '../../../Services/DashboardService';
import { Paths } from '../../../Routes/routePaths';
import { useInsightsStyles } from '../../DashBoard/SuperadminInsights/styles';
import { ExportContext, useExcelDownloads } from '../../DashBoard/SuperadminInsights/exportContext';
import { formatCount, formatDate, formatPercent, requestErrorMessage, scrollToAnchor } from '../../DashBoard/SuperadminInsights/utils';
import { destinationForItem, routeForTarget } from '../../DashBoard/SuperadminInsights/attentionTargets';
import {
  Section, Panel, MetricGrid, DataTable, Badge, LearningHubBadge, OnboardingBadge, UtilisationValue, RelativeTime,
  ErrorPanel, EmptyState, overAssignedRowStyle,
} from '../../DashBoard/SuperadminInsights/components/shared';
import InsightsHeader from '../../DashBoard/SuperadminInsights/components/InsightsHeader';
import AttentionList from '../../DashBoard/SuperadminInsights/components/AttentionList';
import InsightsSkeleton from '../../DashBoard/SuperadminInsights/components/InsightsSkeleton';

const dashboardService = new Dashboard();

const FATAL_TITLES = {
  401: 'Please sign in again',
  403: 'Only superadmins can view this page',
  404: 'Customer not found',
  400: 'This request was not accepted',
};

/** Attention checks scoped to one customer, mapped to the table on this page that lists the records. */
const ATTENTION_ANCHORS = {
  over_assigned_licences: 'customer-projects',
  overdue_projects: 'customer-projects',
  idle_projects: 'customer-projects',
  learning_hub_plans_ending: 'customer-projects',
  stalled_seats: 'customer-learners',
  learners_not_in_project: 'customer-learners',
};

const STEP_STATUS = {
  complete: { Icon: CheckCircleIcon, color: '#16a34a', tone: 'green', label: 'Complete' },
  skipped: { Icon: RemoveCircleOutlineIcon, color: '#94a3b8', tone: 'grey', label: 'Skipped' },
  in_progress: { Icon: TimelapseIcon, color: '#3D7DDD', tone: 'blue', label: 'In progress' },
  pending: { Icon: RadioButtonUncheckedIcon, color: '#94a3b8', tone: 'grey', label: 'Pending' },
};

const compareValues = (left, right) => {
  const emptyLeft = left === null || left === undefined || left === '';
  const emptyRight = right === null || right === undefined || right === '';
  if (emptyLeft || emptyRight) return emptyLeft === emptyRight ? 0 : emptyLeft ? 1 : -1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  if (typeof left === 'boolean' || typeof right === 'boolean') return Number(left) - Number(right);
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
};

const sortValue = (row, key) => (Array.isArray(row[key]) ? row[key].join(', ') : row[key]);

const OnboardingStepper = ({ onboarding = {} }) => {
  const classes = useInsightsStyles();
  const steps = onboarding.steps || [];
  if (!steps.length) return <EmptyState>{onboarding.label}</EmptyState>;
  const activeIndex = Math.max(0, steps.findIndex(step => step.key === onboarding.current_step));
  return (
    <div style={{ overflowX: 'auto' }}>
      <Stepper alternativeLabel activeStep={activeIndex} style={{ background: 'transparent', padding: '8px 0', minWidth: steps.length * 120 }} aria-label="Onboarding steps">
        {steps.map(step => {
          const status = STEP_STATUS[step.status] || STEP_STATUS.pending;
          const { Icon } = status;
          return (
            <Step key={step.key} completed={step.status === 'complete' || step.status === 'skipped'}>
              <StepLabel StepIconComponent={() => <Icon style={{ color: status.color }} aria-hidden="true" />}>
                <Typography style={{ fontSize: '.8rem', fontWeight: step.key === onboarding.current_step ? 800 : 600 }}>{step.label}</Typography>
                <Badge tone={status.tone}>{status.label}</Badge>
                {step.completed_at ? <Typography className={classes.description} style={{ marginTop: 2 }}>{formatDate(step.completed_at)}</Typography> : null}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
};

const LearnersTable = ({ learners = [], truncated }) => {
  const classes = useInsightsStyles();
  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue(query);
  const [sort, setSort] = React.useState({ key: 'name', direction: 'asc' });
  const rows = React.useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase();
    const filtered = needle
      ? learners.filter(row => [row.name, row.email, ...(row.projects || [])].some(value => String(value || '').toLowerCase().includes(needle)))
      : learners;
    const sorted = [...filtered].sort((a, b) => compareValues(sortValue(a, sort.key), sortValue(b, sort.key)));
    return sort.direction === 'asc' ? sorted : sorted.reverse();
  }, [learners, debouncedQuery, sort]);
  const sortable = (label, key) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.direction : 'asc'}
      onClick={() => setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' }))}
    >{label}</TableSortLabel>
  );
  const columns = [
    { key: 'name', label: sortable('Name', 'name'), render: row => <span style={{ fontWeight: 600 }}>{row.name || '-'}</span> },
    { key: 'email', label: sortable('Email', 'email') },
    { key: 'can_sign_in', label: sortable('Can sign in', 'can_sign_in'), render: row => <Badge tone={row.can_sign_in ? 'green' : 'grey'}>{row.can_sign_in ? 'Yes' : 'No'}</Badge> },
    { key: 'projects', label: sortable('Projects', 'projects'), render: row => (row.projects?.length ? row.projects.join(', ') : '-') },
    { key: 'seats', label: sortable('Seats', 'seats'), align: 'right', render: row => formatCount(row.seats) },
    { key: 'courses_started', label: sortable('Courses started', 'courses_started'), align: 'right', render: row => formatCount(row.courses_started) },
    { key: 'courses_completed', label: sortable('Courses completed', 'courses_completed'), align: 'right', render: row => formatCount(row.courses_completed) },
    { key: 'avg_course_progress_pct', label: sortable('Avg progress', 'avg_course_progress_pct'), align: 'right', render: row => formatPercent(row.avg_course_progress_pct) },
    { key: 'last_sign_in_at', label: sortable('Last sign-in', 'last_sign_in_at'), render: row => <RelativeTime value={row.last_sign_in_at} /> },
  ];
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Search name, email, or project"
          value={query}
          onChange={event => setQuery(event.target.value)}
          inputProps={{ 'aria-label': 'Search learners' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          style={{ minWidth: 280 }}
        />
        <Typography variant="caption" color="textSecondary">{formatCount(rows.length)} of {formatCount(learners.length)} shown</Typography>
      </div>
      {truncated ? <Typography className={classes.footnote} style={{ marginTop: 0, marginBottom: 8, color: '#b45309', fontWeight: 600 }}>Showing first 500 — export for the full list</Typography> : null}
      <DataTable ariaLabel="Learners" columns={columns} rows={rows} getRowKey={row => row.user_id} />
    </>
  );
};

const CustomerInsightsPage = () => {
  const classes = useInsightsStyles();
  const history = useHistory();
  const { customerId } = useParams();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [fatal, setFatal] = React.useState(null);
  const requestId = React.useRef(0);
  const pageRef = React.useRef(null);
  const { busy, download } = useExcelDownloads();

  React.useEffect(() => {
    if (pageRef.current?.scrollIntoView) pageRef.current.scrollIntoView({ block: 'start' });
  }, [customerId]);

  const load = React.useCallback(async (refresh = false) => {
    const currentRequest = ++requestId.current;
    if (refresh) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const response = await dashboardService.getSuperadminCustomerInsights(customerId, refresh);
      if (currentRequest !== requestId.current) return;
      if (!response?.status || !response.data) throw new Error(response?.message || 'Something went wrong');
      setFatal(null);
      setData(response.data);
    } catch (requestError) {
      if (currentRequest !== requestId.current) return;
      const status = requestError?.response?.status;
      const message = requestErrorMessage(requestError);
      if (FATAL_TITLES[status]) {
        setFatal({ status, message });
        setData(null);
        return;
      }
      setError(message);
    } finally {
      if (currentRequest === requestId.current) { setLoading(false); setRefreshing(false); }
    }
  }, [customerId]);

  React.useEffect(() => {
    setData(null);
    load();
    return () => { requestId.current += 1; };
  }, [load]);

  const exporter = React.useMemo(() => ({
    busy,
    exportSection: key => download(key, () => dashboardService.exportSuperadminCustomerInsights(customerId), 'customer.xlsx'),
  }), [busy, download, customerId]);

  const openAttention = item => {
    if (scrollToAnchor(ATTENTION_ANCHORS[item.key])) return;
    if (item.key === 'onboarding_stuck' || item.key === 'customers_without_projects') {
      const route = routeForTarget({ page: 'onboarding', customer_id: customerId, step: item.key === 'customers_without_projects' ? 'projects' : undefined });
      if (route) { history.push(route); return; }
    }
    const destination = destinationForItem(item);
    history.push(destination.route || Paths.Dashboard);
  };

  const openProject = row => {
    const route = routeForTarget(row?.drilldown?.target || row?.links?.project?.target || {
      page: 'project', project_id: row?.project_id,
    });
    if (route) history.push(route);
  };

  const backLink = (
    <Button component={RouterLink} to={Paths.Dashboard} size="small" startIcon={<ArrowBackIcon fontSize="small" />} style={{ marginBottom: 12 }}>
      Back to dashboard
    </Button>
  );

  let content;
  if (fatal) {
    content = (
      <ErrorPanel
        title={FATAL_TITLES[fatal.status]}
        message={fatal.message}
        action={fatal.status === 401 ? <Button variant="contained" color="primary" onClick={() => history.replace(Paths.Login)}>Sign in</Button> : null}
      />
    );
  } else if (loading && !data) {
    content = <InsightsSkeleton tiles={9} sections={4} label="Loading customer insights" />;
  } else if (!data) {
    content = <ErrorPanel title="We couldn't load this customer" message={error} action={<Button variant="contained" color="primary" onClick={() => load()}>Try again</Button>} />;
  } else {
    const { meta = {}, customer = {} } = data;
    const onboarding = customer.onboarding || {};
    const seatColumns = [
      { key: 'label', label: 'Type', render: row => <strong>{row.label}</strong> },
      { key: 'granted', label: 'Granted', align: 'right', render: row => formatCount(row.granted) },
      { key: 'allocated', label: 'Allocated', align: 'right', render: row => formatCount(row.allocated) },
      { key: 'assigned', label: 'Assigned', align: 'right', render: row => formatCount(row.assigned) },
      { key: 'unassigned_in_projects', label: 'Unassigned in projects', align: 'right', render: row => formatCount(row.unassigned_in_projects) },
      { key: 'utilisation_pct', label: 'Utilisation', render: row => <UtilisationValue value={row.utilisation_pct} overAssigned={row.over_assigned} /> },
    ];
    const projectColumns = [
      { key: 'project', label: 'Project', render: row => <span style={{ fontWeight: 600 }}>{row.project}</span> },
      { key: 'status', label: 'Status', render: row => <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>{row.status || '-'}{row.overdue ? <Badge tone="amber">Overdue</Badge> : null}</span> },
      { key: 'dates', label: 'Start – end', render: row => `${formatDate(row.start_date)} – ${formatDate(row.end_date)}` },
      { key: 'learners', label: 'Learners', align: 'right', render: row => formatCount(row.learners) },
      { key: 'seats', label: 'Seats assigned / allocated', align: 'right', render: row => `${formatCount(row.seats_assigned)} / ${formatCount(row.seats_allocated)}` },
      { key: 'utilisation_pct', label: 'Utilisation', render: row => <UtilisationValue value={row.utilisation_pct} overAssigned={row.over_assigned} /> },
      { key: 'courses', label: 'Courses started / completed', align: 'right', render: row => `${formatCount(row.courses_started)} / ${formatCount(row.courses_completed)}` },
      {
        key: 'learning_hub',
        label: 'Learning Hub',
        render: row => (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <LearningHubBadge value={row.learning_hub} />
            {row.learning_hub_days_left !== null && row.learning_hub_days_left !== undefined
              ? <span style={Number(row.learning_hub_days_left) <= 14 ? { color: '#b45309', fontWeight: 700 } : undefined}>{formatCount(row.learning_hub_days_left)} days left</span>
              : null}
          </span>
        ),
      },
    ];
    const courseColumns = [
      { key: 'course', label: 'Course', render: row => <span style={{ fontWeight: 600 }}>{row.course}</span> },
      { key: 'provider', label: 'Provider' },
      { key: 'assigned', label: 'Assigned', align: 'right', render: row => formatCount(row.assigned) },
      { key: 'started', label: 'Started', align: 'right', render: row => formatCount(row.started) },
      { key: 'completed', label: 'Completed', align: 'right', render: row => formatCount(row.completed) },
    ];
    content = (
      <ExportContext.Provider value={exporter}>
        {refreshing ? <LinearProgress className={classes.progress} aria-label="Updating customer insights" /> : null}
        {error ? <Panel role="alert" style={{ marginBottom: 16, borderColor: '#dc2626' }}><Typography style={{ fontWeight: 700 }}>We couldn&apos;t update this customer</Typography><Typography color="textSecondary">{error}</Typography></Panel> : null}
        <InsightsHeader
          eyebrow="Customer"
          title={customer.name || '-'}
          meta={meta}
          onRefresh={() => load(true)}
          refreshing={refreshing}
          exportKey="customer"
          exportLabel="Export customer"
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            {customer.partner_label ? <Badge tone="blue">{customer.partner_label}</Badge> : null}
            {onboarding.label ? <OnboardingBadge status={onboarding.status} label={onboarding.label} /> : null}
            {customer.customer_since ? <Typography variant="caption" color="textSecondary">Customer since {formatDate(customer.customer_since)}</Typography> : null}
            {customer.email ? <Typography variant="caption" color="textSecondary">{customer.email}</Typography> : null}
          </div>
        </InsightsHeader>
        <div className={refreshing ? classes.loadingOverlay : undefined}>
          <div style={{ marginTop: 24 }}><MetricGrid items={data.totals || []} columns={3} /></div>
          <AttentionList id="customer-attention" items={data.attention || []} onAction={openAttention} />
          <Section id="customer-onboarding" title="Onboarding" subtitle={onboarding.label}>
            <Panel><OnboardingStepper onboarding={onboarding} /></Panel>
          </Section>
          <Section id="customer-seats" title="Seats by type">
            <Panel><DataTable ariaLabel="Seats by type" columns={seatColumns} rows={data.seats_by_type || []} /></Panel>
          </Section>
          <Section id="customer-projects" title="Projects">
            <Panel>
              {meta.projects_truncated ? <Typography className={classes.footnote} style={{ marginTop: 0, marginBottom: 8, color: '#b45309', fontWeight: 600 }}>Showing first 200 — export for the full list</Typography> : null}
              <DataTable ariaLabel="Projects" columns={projectColumns} rows={data.projects || []} onRowClick={openProject} rowStyle={overAssignedRowStyle} getRowKey={row => row.project_id} />
            </Panel>
          </Section>
          <Section id="customer-courses" title="Courses">
            <Panel><DataTable ariaLabel="Courses" columns={courseColumns} rows={data.courses || []} getRowKey={row => row.course_id} /></Panel>
          </Section>
          <Section id="customer-learners" title="Learners">
            <Panel><LearnersTable learners={data.learners || []} truncated={meta.learners_truncated} /></Panel>
          </Section>
        </div>
      </ExportContext.Provider>
    );
  }

  return (
    <Layout>
      <main className={classes.page} ref={pageRef}>
        {backLink}
        {content}
      </main>
    </Layout>
  );
};

export default CustomerInsightsPage;
